import {
  Column,
  Filter,
  FilterArguments,
  FilterCallback,
  OperatorType,
  SearchTerm,
} from 'angular-slickgrid';
import {
  ApplicationRef,
  ComponentRef,
  EnvironmentInjector,
  Injector,
  createComponent,
} from '@angular/core';
import { ZnMultiSelect } from '../../zn-select/zn-multi-select';
import { ZendraLocalizationServices } from '../../enums/abpLocalizationServices';
import { ZnSelectQueryParams } from '../../generic-form/interfaces/IGenericFieldOptions';

type ZnSelectFilterParams = {
  injector?: Injector;
  optionsLoader?: (params?: ZnSelectQueryParams) => any;
  errorEntityKey?: string;
};

export class ZnSelectFilter implements Filter {
  grid: any;
  searchTerms: SearchTerm[] = [];
  columnDef!: Column;
  callback!: FilterCallback;
  operator: OperatorType = OperatorType.in;

  private filterElm?: HTMLDivElement;
  private componentRef?: ComponentRef<ZnMultiSelect>;
  private appRef?: ApplicationRef;
  private envInjector?: EnvironmentInjector;
  private selectionSubscription?: { unsubscribe: () => void };
  private pendingValues: SearchTerm[] | null = null;

  init(args: FilterArguments): void {
    if (!args) {
      throw new Error(
        '[Angular-Slickgrid] A filter must always have an "init()" with valid arguments.'
      );
    }

    this.grid = args.grid;
    this.columnDef = args.columnDef;
    this.callback = args.callback;
    this.operator = (args.operator as OperatorType) || OperatorType.in;
    this.searchTerms = args.searchTerms || [];

    const params = (this.columnDef.filter?.params || {}) as ZnSelectFilterParams;
    const injector = params.injector;
    if (!injector) {
      return;
    }

    this.appRef = injector.get(ApplicationRef);
    this.envInjector = injector.get(EnvironmentInjector);

    this.filterElm = document.createElement('div');
    this.filterElm.classList.add('zn-select-filter');
    args.filterContainerElm.appendChild(this.filterElm);

    this.componentRef = createComponent(ZnMultiSelect, {
      environmentInjector: this.envInjector,
      hostElement: this.filterElm,
    });
    this.appRef.attachView(this.componentRef.hostView);
    this.componentRef.changeDetectorRef.detectChanges();

    if (params.optionsLoader) {
      this.componentRef.setInput('optionsLoader', this.createContextualOptionsLoader(params.optionsLoader));
    }
    if (params.errorEntityKey) {
      this.componentRef.setInput('errorEntityKey', params.errorEntityKey);
    }
    this.componentRef.setInput('showSearch', true);
    this.componentRef.setInput('showClearSelection', false);
    this.componentRef.setInput('localizationResource', ZendraLocalizationServices.LanguageService);

    this.selectionSubscription = this.componentRef.instance.selectedChange.subscribe(value => {
      const terms = Array.isArray(value) ? value : value ? [value] : [];
      this.searchTerms = terms;
      if (!terms.length) {
        this.callback(undefined, {
          columnDef: this.columnDef,
          operator: OperatorType.empty,
          searchTerms: [],
          shouldTriggerQuery: true,
        });
      } else {
        this.callback(undefined, {
          columnDef: this.columnDef,
          operator: OperatorType.in,
          searchTerms: terms,
          shouldTriggerQuery: true,
        });
      }
    });

    if (this.searchTerms?.length) {
      this.setValues(this.searchTerms);
    }
  }

  clear(shouldTriggerQuery = true): void {
    this.searchTerms = [];
    if (this.componentRef) {
      this.componentRef.instance.writeValue([]);
    } else {
      this.pendingValues = [];
    }

    if (shouldTriggerQuery) {
      this.callback(undefined, {
        columnDef: this.columnDef,
        operator: OperatorType.empty,
        searchTerms: [],
        shouldTriggerQuery: true,
      });
    }
  }

  destroy(): void {
    this.selectionSubscription?.unsubscribe();
    this.selectionSubscription = undefined;

    if (this.componentRef) {
      this.appRef?.detachView(this.componentRef.hostView);
      this.componentRef.destroy();
      this.componentRef = undefined;
    }

    if (this.filterElm) {
      this.filterElm.remove();
      this.filterElm = undefined;
    }
  }

  getValues(): any {
    return this.searchTerms;
  }

  setValues(values: SearchTerm[] | SearchTerm): void {
    const terms = Array.isArray(values) ? values : values ? [values] : [];
    this.searchTerms = terms;
    const stringTerms = terms.map(term => String(term));
    if (!this.componentRef) {
      this.pendingValues = stringTerms;
      return;
    }

    this.componentRef.instance.writeValue(stringTerms);
    this.pendingValues = null;
  }

  private createContextualOptionsLoader(
    optionsLoader: NonNullable<ZnSelectFilterParams['optionsLoader']>
  ): (params?: ZnSelectQueryParams) => any {
    return (params?: ZnSelectQueryParams) =>
      optionsLoader({
        ...params,
        filterContext: this.getFilterContext(),
      });
  }

  private getFilterContext(): Record<string, unknown> {
    // SlickGrid passes its own grid instance to custom filters, whereas the
    // AngularGridInstance exposes the backend service directly. Resolve both
    // shapes: in production this is normally available through the grid
    // options configured by GenericTable.
    const grid = this.grid as any;
    const backendService =
      grid?.backendService ??
      grid?.backendServiceApi?.service ??
      grid?.gridService?.backendServiceApi?.service ??
      grid?.options?.backendServiceApi?.service ??
      grid?.getOptions?.()?.backendServiceApi?.service;
    const columnId = this.columnDef?.id ?? this.columnDef?.field;

    return (backendService as
      | { getFilterContext?: (excludeColumnId?: string) => Record<string, unknown> }
      | undefined)?.getFilterContext?.(
      columnId === undefined ? undefined : String(columnId)
    ) ?? {};
  }
}
